/**
 * Buyer / org URL slug — shared by the API (which computes it once from the
 * registry name), the web canonical, and the prerender/sitemap, so all three
 * agree on the exact string. INN-FIRST: `{tin}-{name-slug}`.
 *
 * WHY INN-first (not name-first like the tender slug). The ИНН is the buyer's
 * stable public key and is always a valid bare URL on its own, so `/buyer/{tin}`
 * (every URL already indexed) stays a clean prefix of the slugged form and keeps
 * resolving — {@link extractOrgTin} recovers the ИНН from the leading digits with
 * no dependence on the name. The name is decoration for the crawler and the
 * click; the ИНН is the identity.
 *
 * The name half is derived from the REGISTRY name only (not the source-fallback
 * display name), because that is the one value the profile endpoint and the
 * `/buyers/index` list resolve identically — decoupling the slug from the
 * fallback is what guarantees the canonical, the sitemap entry and the
 * prerendered file are byte-identical URLs. A buyer with no registry name gets
 * the bare `{tin}` (exactly its URL today — no migration for that subset).
 */

// Cyrillic (Russian + Uzbek) → Latin, for a readable ASCII slug. Multi-char
// first so `щ`/`ю` etc. resolve before the single-letter pass.
const TRANSLIT: ReadonlyArray<readonly [string, string]> = [
  ['щ', 'shch'],
  ['ю', 'yu'],
  ['я', 'ya'],
  ['ж', 'zh'],
  ['ч', 'ch'],
  ['ш', 'sh'],
  ['ё', 'yo'],
  ['ъ', ''],
  ['ь', ''],
  ['ў', "o'"],
  ['қ', 'q'],
  ['ғ', "g'"],
  ['ҳ', 'h'],
  ['а', 'a'],
  ['б', 'b'],
  ['в', 'v'],
  ['г', 'g'],
  ['д', 'd'],
  ['е', 'e'],
  ['з', 'z'],
  ['и', 'i'],
  ['й', 'y'],
  ['к', 'k'],
  ['л', 'l'],
  ['м', 'm'],
  ['н', 'n'],
  ['о', 'o'],
  ['п', 'p'],
  ['р', 'r'],
  ['с', 's'],
  ['т', 't'],
  ['у', 'u'],
  ['ф', 'f'],
  ['х', 'x'],
  ['ц', 'ts'],
  ['ы', 'i'],
  ['э', 'e'],
];

// Legal-form words dropped from the slug — they carry no keyword value and,
// leading, would eat the whole length cap («общество с ограниченной…»). Matched
// as whole tokens after transliteration, so both Cyrillic-origin and native
// Latin spellings are covered. Kept lowercase, apostrophes stripped.
const STOPWORDS = new Set([
  // ru abbreviations + transliterated phrase words
  'ooo',
  'oao',
  'zao',
  'ao',
  'gup',
  'dup',
  'up',
  'gp',
  'chp',
  'sp',
  'fh',
  'obshchestvo',
  's',
  'ogranichennoy',
  'ogranichennoi',
  'otvetstvennostyu',
  'aktsionernoe',
  'aksionernoe',
  'otkrytoe',
  'zakrytoe',
  'gosudarstvennoe',
  'unitarnoe',
  'predpriyatie',
  'chastnoe',
  'sovmestnoe',
  'semeynoe',
  // uz Latin
  'mchj',
  'aj',
  'oaj',
  'duk',
  'xk',
  'ok',
  'yatt',
  'masuliyati',
  'cheklangan',
  'jamiyati',
  'jamiyat',
  'aksiyadorlik',
  'ochiq',
  'yopiq',
  'davlat',
  'unitar',
  'korxonasi',
  'korxona',
  'xususiy',
  'oilaviy',
  'fermer',
  'xojaligi',
  'qoshma',
]);

const MAX_SLUG_LEN = 60;

function transliterate(input: string): string {
  let s = input.toLowerCase();
  // Fold apostrophe variants so `o'zbek` → `ozbek` (the apostrophe is dropped by
  // slugification anyway; folding first keeps `o'` and `oʻ` identical).
  s = s.replace(/[ʻʼ'’`´]/g, '');
  for (const [from, to] of TRANSLIT) s = s.split(from).join(to);
  return s;
}

/** The name half of the slug: transliterated, legal-form-stripped, hyphenated. */
export function slugifyOrgName(name: string | null | undefined): string {
  if (!name) return '';
  const tokens = transliterate(name)
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter((tok) => tok.length > 0 && !STOPWORDS.has(tok));
  let slug = '';
  for (const tok of tokens) {
    const next = slug ? `${slug}-${tok}` : tok;
    if (next.length > MAX_SLUG_LEN) break;
    slug = next;
  }
  return slug;
}

/** Full buyer path slug segment: `{tin}-{name}`, or bare `{tin}` when the name
 *  slugifies to nothing (no registry name, or all stopwords). */
export function orgSlug(tin: string, name: string | null | undefined): string {
  const base = slugifyOrgName(name);
  return base ? `${tin}-${base}` : tin;
}

/**
 * Recover the ИНН from a `/buyer/:seg` (or `/supplier/:seg`) route param that
 * may be slugged (`{tin}-{name}`) or bare (`{tin}`). The ИНН is the LEADING run
 * of digits — an org name never starts with the buyer's own ИНН, and the slug is
 * INN-first by construction. Returns the whole param when it has no leading
 * digits (defensive; never happens for a real buyer URL).
 */
export function extractOrgTin(param: string): string {
  const m = param.match(/^(\d+)/);
  return m ? m[1] : param;
}
